import {
  AdminExtendedUser,
  AdminService,
  InternalAccountRole,
} from '@admin-api/index';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'user-list-transfer-ownership',
  templateUrl: './transfer-ownership.component.html',
  styleUrls: ['./transfer-ownership.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TransferOwnershipComponent implements OnInit {
  confirmed = false;
  isLoading = false;
  isLoadingAccounts = true;
  errorMessage: string = null;

  /** Non-owner account roles eligible for transfer, with selection state and resolved name. */
  eligibleAccounts: Array<{
    account_id: string;
    role: string;
    account_name: string;
    selected: boolean;
  }> = [];

  get hasMultipleAccounts(): boolean {
    return this.eligibleAccounts.length > 1;
  }

  get selectedAccountIds(): string[] {
    return this.eligibleAccounts
      .filter((a) => a.selected)
      .map((a) => a.account_id);
  }

  private readonly OWNER_ROLES = ['owner', 'merchant_owner', 'reseller_owner'];

  constructor(
    public matDialogRef: MatDialogRef<TransferOwnershipComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ITransferOwnershipDialogData,
    private _adminService: AdminService,
  ) {}

  ngOnInit(): void {
    // Fetch the user by ID to get ALL account_roles.
    // The list endpoint's LIMIT + subQuery:false truncates JOINed account_users rows.
    this._adminService
      .adminGetUser({ id: this.data.target_user_id })
      .subscribe({
        next: (fullUser) => {
          const accountRoles = this.mergeAccountRoles(fullUser);
          this.buildEligibleAccounts(accountRoles);
          this.isLoadingAccounts = false;
        },
        error: () => {
          // Fall back to the account_roles passed in from the list
          this.buildEligibleAccounts(this.data.account_roles);
          this.isLoadingAccounts = false;
        },
      });
  }

  /**
   * Merges account_roles (from account_users table) with the legacy
   * users.account_id/users.role pair, which may not have a corresponding
   * account_users row.
   */
  private mergeAccountRoles(
    user: AdminExtendedUser | undefined,
  ): Array<InternalAccountRole> {
    const roles: Array<InternalAccountRole> = [
      ...(user?.account_roles ?? this.data.account_roles),
    ];

    // Include the legacy users.account_id/role if not already in account_roles
    if (
      user?.account_id &&
      user?.role &&
      !roles.some((ar) => ar.account_id === user.account_id)
    ) {
      roles.push({
        account_id: user.account_id,
        role: user.role,
      });
    }

    return roles;
  }

  private buildEligibleAccounts(
    accountRoles: Array<InternalAccountRole>,
  ): void {
    const eligible = accountRoles
      .filter((ar) => !this.OWNER_ROLES.includes(ar.role));

    this.eligibleAccounts = eligible
      .map((ar) => ({
        account_id: ar.account_id,
        role: ar.role,
        account_name: ar.account_id,
        selected: eligible.length === 1,
      }));

    if (this.eligibleAccounts.length === 0) {
      return;
    }

    // Resolve all account names in parallel
    const accountRequests = this.eligibleAccounts.map((entry) =>
      this._adminService.adminGetAccount({ id: entry.account_id }),
    );

    forkJoin(accountRequests).subscribe({
      next: (accounts) => {
        accounts.forEach((account, i) => {
          this.eligibleAccounts[i].account_name =
            account.name || this.eligibleAccounts[i].account_id;
        });
      },
    });
  }

  cancel(): void {
    this.matDialogRef.close();
  }

  submit(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this._adminService
      .adminTransferOwnership({
        id: this.data.target_user_id,
        transferOwnershipParams: {
          account_ids: this.selectedAccountIds,
        },
      })
      .subscribe({
        next: () => {
          this.matDialogRef.close({ success: true });
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.message ?? 'Unknown error';
        },
      });
  }
}

export interface ITransferOwnershipDialogData {
  target_user_id: string;
  target_user_name: string;
  target_user_email: string;
  account_roles: Array<InternalAccountRole>;
}
